// Core functionality interfaces
class SearchFeature {
  constructor(host) {
    this.host = host;
  }

  init() {} // Optional initialization
  destroy() {} // Cleanup if needed
}

class PlaceholderManager extends SearchFeature {
  constructor(host) {
    super(host);
    this.placeHolderTexts = window.searchBar.dynamicPrompts.filter(item => item !== "");
    this.placeHolderIndex = 0;
    this.placeHolderTextLetterIndex = 0;
    this.searchBar = host.querySelector("#Search-In-Modal") || host.querySelector('#Search-In-Modal-Mobile')
  }

  init() {
    this.searchBar.setAttribute("placeholder", window.searchBar.staticPrompt);

    if (this.placeHolderTexts.length > 1) {
      this.typeWriterEffect(this.placeHolderTexts[0]);
    } else if (this.placeHolderTexts.length === 1) {
      this.searchBar.setAttribute(
        "placeholder",
        window.searchBar.staticPrompt + this.placeHolderTexts[0]
      );
    }
  }

  typeWriterEffect(string) {
    if (this.placeHolderTextLetterIndex < string.length) {
      const newAttribute = this.searchBar.getAttribute("placeholder") +
        string.charAt(this.placeHolderTextLetterIndex);
      this.searchBar.setAttribute("placeholder", newAttribute);
      this.placeHolderTextLetterIndex++;
      setTimeout(() => this.typeWriterEffect(string), 75);
    } else {
      setTimeout(() => this.typeDeleterEffect(string), 1000);
    }
  }

  typeDeleterEffect(string) {
    if (this.searchBar.getAttribute("placeholder") !== window.searchBar.staticPrompt) {
      const newAttribute = this.searchBar.getAttribute("placeholder").slice(0, -1);
      this.searchBar.setAttribute("placeholder", newAttribute);
      setTimeout(() => this.typeDeleterEffect(string), 50);
    } else {
      this.placeHolderIndex = (this.placeHolderIndex + 1) % this.placeHolderTexts.length;
      this.placeHolderTextLetterIndex = 0;
      this.typeWriterEffect(this.placeHolderTexts[this.placeHolderIndex]);
    }
  }
}

class ProductTypeFilter extends SearchFeature {
  constructor(host) {
    super(host);
    this.productTypeSelector = host.querySelector('.js-product-type');
    this.input = host.querySelector('input[type="search"]');
    this.boundFilterChange = this.handleFilterChange.bind(this);
  }

  init() {
    if (this.productTypeSelector) {
      this.productTypeSelector.addEventListener('change', this.boundFilterChange);
    }
  }

  destroy() {
    if (this.productTypeSelector) {
      this.productTypeSelector.removeEventListener('change', this.boundFilterChange);
    }
  }

  handleFilterChange() {
    this.applyFilter();
    // Trigger search with new filter if there's an active search
    if (this.input.value.trim()) {
      // If predictive search is enabled, trigger it
      if (!this.host.dataset?.disabled) {
        this.host.features.search.onChange();
      }
    }
  }

  getFilter() {
    return this.productTypeSelector?.value.replace(' ', '+') || '';
  }

  applyFilter() {
    const cloneNode = this.input.cloneNode(true);
    cloneNode.setAttribute("type", "hidden");
    cloneNode.value = '';

    const productTypeFilter = this.getFilter();

    if (productTypeFilter !== '') {
      cloneNode.value += "product_type:" + productTypeFilter;
      if (this.input.value !== '') {
        cloneNode.value += " AND ";
      }
    }
    cloneNode.value += this.input.value;

    this.input.removeAttribute("name");
    this.input.insertAdjacentElement("afterend", cloneNode);
  }
}

class KeyboardManager extends SearchFeature {
  constructor(host) {
    super(host);
    this.input = host.querySelector('input[type="search"]');
    this.boundShortcutHandler = this.handleShortcut.bind(this);
  }

  init() {
    document.addEventListener("keydown", this.boundShortcutHandler);
  }

  destroy() {
    document.removeEventListener("keydown", this.boundShortcutHandler);
  }

  handleShortcut(event) {
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    const key = isMac ? "Meta" : "Control";

    if (event.key === "/" || (event.key === "k" && (event.metaKey || event.ctrlKey))) {
      event.preventDefault();
      this.input.focus();
    }
  }
}

class PredictiveSearchCore extends SearchFeature {
  constructor(host) {
    super(host);
    this.cachedResults = {};
    this.input = host.querySelector('input[type="search"]');
    this.predictiveSearchResults = host.querySelector(".js-search-output-wrapper");
    this.outputWrapper = host.querySelector(".js-search-output-wrapper");
    this.statusElement = host.querySelector(".predictive-search-status");
    this.isOpen = false;
    this.noResults = false;
    this.show_collections = host.dataset.collections === 'true';

    this.boundHandlers = {
      onChange: debounce(this.onChange.bind(this), 300),
      onFocus: this.onFocus.bind(this),
      onFocusOut: this.onFocusOut.bind(this),
      onKeyup: this.onKeyup.bind(this),
      onKeydown: this.onKeydown.bind(this)
    };
  }

  init() {
    if (this.host.dataset?.disabled) return;

    this.setupEventListeners();
    this.saveSuggestions();
  }

  destroy() {
    this.removeEventListeners();
  }

  setupEventListeners() {
    this.input.addEventListener("input", this.boundHandlers.onChange);
    this.input.addEventListener("focus", this.boundHandlers.onFocus);
    this.host.addEventListener("focusout", this.boundHandlers.onFocusOut);
    this.host.addEventListener("keyup", this.boundHandlers.onKeyup);
    this.host.addEventListener("keydown", this.boundHandlers.onKeydown);
  }

  removeEventListeners() {
    this.input.removeEventListener("input", this.boundHandlers.onChange);
    this.input.removeEventListener("focus", this.boundHandlers.onFocus);
    this.host.removeEventListener("focusout", this.boundHandlers.onFocusOut);
    this.host.removeEventListener("keyup", this.boundHandlers.onKeyup);
    this.host.removeEventListener("keydown", this.boundHandlers.onKeydown);
  }

  saveSuggestions() {
    this.suggestions = this.outputWrapper.innerHTML;
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    this.host.querySelector(".js-error-message").style.display = "";

    if (!searchTerm.length) {
      this.showSuggestions();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showSuggestions();
      return;
    }

    if (this.host.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut(e) {
    if (e.target.getAttribute("aria-label") === "minus" ||
        e.target.getAttribute("aria-label") === "delete") return;

    setTimeout(() => {
      if (!this.host.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    event.preventDefault();

    switch (event.code) {
      case "Escape":
        this.close();
        break;
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  showSuggestions() {
    if (this.host.dataset?.disabled) return;
    this.outputWrapper.innerHTML = this.suggestions;
    this.host.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
  }

  switchOption(direction) {
    if (!this.host.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.host.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.host.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);

    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedElementIndex = allVisibleElements.findIndex(
      element => element === selectedElement
    );

    if (this.statusElement) this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.host.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) selectedProduct.click();
  }

  async getSearchResults(searchTerm) {
    let queryKey = searchTerm.replace(" ", "+").toLowerCase();
    const productTypeFilter = this.host.features.productTypeFilter?.getFilter() || '';

    if (productTypeFilter !== '') {
      queryKey = `product_type:${productTypeFilter} AND ${queryKey}`;
    }

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.noResults = false;
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(
          queryKey
        )}&${encodeURIComponent(
          "resources[type]"
        )}=product${this.show_collections ? ',collection' : ''}&section_id=api--search--predictive`
      );

      if (!response.ok) {
        throw new Error(response.status);
      }

      const text = await response.text();
      const resultsMarkup = new DOMParser()
        .parseFromString(text, "text/html")
        .querySelector("#shopify-section-api--search--predictive");

      const noResultsItem = resultsMarkup.querySelector("#no-results");
      const noResultsText = this.host.querySelector(".js-error-message");

      this.noResults = false;
      if (noResultsItem) {
        this.noResults = true;
        noResultsText.innerHTML = noResultsItem.innerHTML;
      } else {
        this.cachedResults[queryKey] = resultsMarkup.innerHTML;
        noResultsText.innerHTML = "";
      }

      this.renderSearchResults(resultsMarkup.innerHTML);
    } catch (error) {
      this.close();
      throw error;
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.host.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.host.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.host.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.noResults) {
      this.host.querySelector(".js-error-message").style.display = "block";
      this.outputWrapper.innerHTML = "";
    } else {
      this.host.querySelector(".js-error-message").style.display = "";
      this.outputWrapper.innerHTML = resultsMarkup;
      this.host.setAttribute("results", true);
      this.setLiveRegionResults();
      this.open();
    }
  }

  setLiveRegionResults() {
    this.host.removeAttribute("loading");
    this.setLiveRegionText(
      this.host.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  open() {
    this.host.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.host.removeAttribute("results");
      this.predictiveSearchResults.innerHTML = "";
    }

    const selected = this.host.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.host.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.isOpen = false;
  }
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    // Initialize features
    this.features = {
      placeholders: new PlaceholderManager(this),
      productTypeFilter: new ProductTypeFilter(this),
      keyboard: new KeyboardManager(this),
      search: new PredictiveSearchCore(this)
    };
  }

connectedCallback() {
    // Initialize all features that should work regardless of disabled state
    this.features.placeholders.init();
    this.features.keyboard.init();
    this.features.productTypeFilter.init(); // Initialize product type filter

    // Initialize predictive search only if not disabled
    if (!this.dataset?.disabled) {
      this.features.search.init();
    }

    // Set up form submission handling
    const form = this.querySelector('form[role="search"]');
    form.addEventListener("submit", this.handleFormSubmit.bind(this));
  }

  disconnectedCallback() {
    // Cleanup all features
    Object.values(this.features).forEach(feature => feature.destroy?.());
  }

  handleFormSubmit(event) {
    if (this.dataset?.disabled) {
      event.preventDefault();
      this.removeAttribute("data-disabled");
      setTimeout(() => {
        this.closest('form').submit();
      }, 1000);
      return;
    }

    this.features.productTypeFilter.applyFilter();

    if (!this.features.search.getQuery().length ||
        this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
